import RefreshIcon from "@mui/icons-material/RefreshRounded";
import { Box, Button, Stack } from "@mui/material";
import objectHash from "object-hash";
import React, { useCallback, useMemo, useReducer } from "react";
import type { ApiResponse, PaginatedList } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { wait } from "@entrilia/shared/utilities/promiseHelper";
import type { ApplicationLogs, LogsQueryParams } from "../../../api/types/logTypes";
import GridPaginationFooter from "../GridPaginationFooter";
import LogsSeverityFilter from "./LogsSeverityFilter";
import PeriodFilter from "./PeriodFilter";
import { getInitialState, reducer, stateToRequestParams } from "./syncLogsState";

interface Props<T extends ApplicationLogs> {
  renderTable: (logItems: T[]) => React.ReactElement;
  fetchLogsFromApi: (params: LogsQueryParams) => Promise<ApiResponse<PaginatedList<T>>>;
  hideSeverityFilter?: boolean;
}

const LogsWrapper = <T extends ApplicationLogs>({ renderTable, fetchLogsFromApi, hideSeverityFilter }: Props<T>) => {
  const [state, dispatch] = useReducer(reducer, getInitialState());

  const fetchPayload = useMemo(() => stateToRequestParams(state), [state]);

  const fetchPayloadHash = objectHash(fetchPayload, { unorderedArrays: true });
  const getLogs = useCallback(
    () => fetchLogsFromApi(fetchPayload),
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [fetchPayloadHash]
  );

  const [logs, apiError, { setData: setLogs, fetch: fetchLogs }] = useFetch(getLogs, {
    onSetData: (data) => dispatch({ type: "SET_TOTALS", totalItems: data.total, totalPages: data.totalPages }),
  });

  const handleRefreshClick = async () => {
    setLogs(undefined);
    await wait(100);
    if (state.page !== 0) {
      dispatch({ type: "PAGE_CHANGE", page: 0 });
    } else {
      await fetchLogs();
    }
  };

  if (apiError !== undefined) {
    logError(apiError, "Logs");
    return <DataLoadingFailed title="Loading logs failed" />;
  }

  if (logs === undefined) {
    return <InlineLoader />;
  }

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Stack direction="row" spacing={3} sx={{ alignItems: "center" }}>
          {!hideSeverityFilter && (
            <LogsSeverityFilter
              value={state.severityFilter}
              onChange={(filterValue) => dispatch({ type: "SEVERITY_CHANGE", filterValue })}
            />
          )}

          <PeriodFilter
            value={state.daysPeriod}
            onChange={(daysPeriod) => dispatch({ type: "PERIOD_CHANGE", daysPeriod })}
          />
        </Stack>
        <Button startIcon={<RefreshIcon />} variant="outlined" color="secondary" onClick={handleRefreshClick}>
          Refresh
        </Button>
      </Box>
      {renderTable(logs.items)}
      <GridPaginationFooter
        totalPages={state.totalPages}
        rowsPerPage={state.rowsPerPage}
        pageIndex={state.page}
        onRowsPerPageChange={(rowsPerPage) => dispatch({ type: "ROWS_CHANGE", rowsPerPage })}
        onPageIndexChange={(page) => dispatch({ type: "PAGE_CHANGE", page })}
      />
    </>
  );
};

export default LogsWrapper;
